// ═══════════════════════════════════════════════════
//  Guillotine Bin-Packing Algorithm
//  Ported from nesting.py lines 85-309
//
//  Placement heuristic : Best Short Side Fit (BSSF)
//  Split heuristic     : Shorter Axis Split (SAS)
//
//  Lives in @estimator/shared so the server's project-wide nesting run and
//  the client's per-component nesting preview use ONE implementation. Keep
//  this file free of DB, DOM and framework imports.
// ═══════════════════════════════════════════════════

// ---------------------------------------------------------------------------
// Geometry types
// ---------------------------------------------------------------------------

export interface Part {
  width: number;        // inches
  height: number;       // inches
  label: string;        // e.g. "Kitchen B24 - Left Panel"
  materialId: string;   // groups parts onto same sheets
  canRotate: boolean;   // false when material has grain
  sourceItemId: string; // line_item.id for cost traceability
  sourceArea: string;   // area name
}

export interface PlacedPart {
  part: Part;
  x: number;      // inches from left edge
  y: number;      // inches from bottom edge
  rotated: boolean;
  width: number;  // placed width (swapped if rotated)
  height: number; // placed height
}

export interface FreeRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Sheet {
  width: number;
  height: number;
  materialId: string;
  materialName: string;
  placements: PlacedPart[];
  freeRects: FreeRect[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Minimum usable free-rectangle dimension (inches). */
export const MIN_RECT_DIM = 0.5;

/** Fallback blade kerf (inches) when neither the material nor company settings specify one. */
export const DEFAULT_KERF = 0.125;

// ---------------------------------------------------------------------------
// Helper: resolveNestingParams
// ---------------------------------------------------------------------------

/**
 * Resolve the sheet geometry for a material, matching the precedence used by
 * the project nesting run (nesting.service.ts):
 *   - sheet size  : material sheet_width/sheet_length, else 48 x 96
 *   - border      : material nesting_border, else 0
 *   - kerf        : material nesting_spacing when > 0, else the company kerf
 *
 * Kept here so the server run and the client preview cannot drift apart.
 */
export function resolveNestingParams(
  material: {
    sheet_width?: number | null;
    sheet_length?: number | null;
    nesting_border?: number | null;
    nesting_spacing?: number | null;
  },
  companyKerf?: number | null,
): { sheetW: number; sheetH: number; border: number; kerf: number } {
  const spacing = material.nesting_spacing ?? 0;
  return {
    sheetW: material.sheet_width || 48,
    sheetH: material.sheet_length || 96,
    border: material.nesting_border || 0,
    kerf: spacing > 0 ? spacing : (companyKerf ?? DEFAULT_KERF),
  };
}

/**
 * A material whose grain direction pins part orientation.
 * Same rule as the server's part-collector hasGrain — keep them identical.
 */
export function hasGrain(material: { grain_direction?: string | null }): boolean {
  return (material.grain_direction ?? 'none') !== 'none';
}

// ---------------------------------------------------------------------------
// Helper: findBestFit
// ---------------------------------------------------------------------------

/**
 * Best Short Side Fit (BSSF): among all free rectangles that can contain the
 * part (with kerf), pick the one that minimises the shorter leftover side.
 *
 * @param freeRects - current free rectangles on the sheet
 * @param pw        - part width (inches)
 * @param ph        - part height (inches)
 * @param kerf      - blade kerf allowance (inches)
 * @param canRotate - whether the part may be rotated 90°
 * @returns index into freeRects and whether to rotate, or null if no fit
 */
export function findBestFit(
  freeRects: FreeRect[],
  pw: number,
  ph: number,
  kerf: number,
  canRotate: boolean,
): { index: number; rotated: boolean } | null {
  let bestIndex = -1;
  let bestRotated = false;
  let bestShortSide = Infinity;

  for (let i = 0; i < freeRects.length; i++) {
    const { w: rw, h: rh } = freeRects[i];

    // --- try original orientation ---
    if (rw >= pw && rh >= ph) {
      // Leftover after kerf (clamped to 0 so exact fits score 0)
      const leftoverW = Math.max(0, rw - pw - kerf);
      const leftoverH = Math.max(0, rh - ph - kerf);
      const shortSide = Math.min(leftoverW, leftoverH);
      if (shortSide < bestShortSide) {
        bestShortSide = shortSide;
        bestIndex = i;
        bestRotated = false;
      }
    }

    // --- try rotated orientation (ph × pw) ---
    if (canRotate && pw !== ph) {
      if (rw >= ph && rh >= pw) {
        const leftoverW = Math.max(0, rw - ph - kerf);
        const leftoverH = Math.max(0, rh - pw - kerf);
        const shortSide = Math.min(leftoverW, leftoverH);
        if (shortSide < bestShortSide) {
          bestShortSide = shortSide;
          bestIndex = i;
          bestRotated = true;
        }
      }
    }
  }

  if (bestIndex === -1) return null;
  return { index: bestIndex, rotated: bestRotated };
}

// ---------------------------------------------------------------------------
// Helper: splitRect
// ---------------------------------------------------------------------------

/**
 * Shorter Axis Split (SAS): after placing a part at (rx, ry) in a free
 * rectangle, split the leftover space into at most two new free rectangles.
 *
 * The split axis is chosen so that the longer leftover dimension runs along
 * the split edge, which tends to keep larger usable regions intact.
 *
 * @returns array of 0-2 new FreeRect entries
 */
export function splitRect(
  rx: number,
  ry: number,
  rw: number,
  rh: number,
  pw: number,
  ph: number,
  kerf: number,
): FreeRect[] {
  const remW = rw - pw - kerf; // space to the right of the placed part
  const remH = rh - ph - kerf; // space above the placed part

  const result: FreeRect[] = [];

  if (remW < remH) {
    // Horizontal split: right rect stays same height as part; top rect spans
    // the full width of the original free rect.
    //
    //   ┌───────┬──────────┐
    //   │  top  │   top    │  ← full width, remH tall
    //   ├───────┼──────────┤
    //   │ part  │  right   │  ← part height, remW wide
    //   └───────┴──────────┘
    if (remW >= MIN_RECT_DIM && ph >= MIN_RECT_DIM) {
      result.push({ x: rx + pw + kerf, y: ry, w: remW, h: ph });
    }
    if (rw >= MIN_RECT_DIM && remH >= MIN_RECT_DIM) {
      result.push({ x: rx, y: ry + ph + kerf, w: rw, h: remH });
    }
  } else {
    // Vertical split: right rect spans the full height of the original free
    // rect; top rect stays same width as part.
    //
    //   ┌───────┬──────────┐
    //   │  top  │          │
    //   ├───────┤  right   │  ← full height, remW wide
    //   │ part  │          │
    //   └───────┴──────────┘
    if (remW >= MIN_RECT_DIM && rh >= MIN_RECT_DIM) {
      result.push({ x: rx + pw + kerf, y: ry, w: remW, h: rh });
    }
    if (pw >= MIN_RECT_DIM && remH >= MIN_RECT_DIM) {
      result.push({ x: rx, y: ry + ph + kerf, w: pw, h: remH });
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Main: guillotinePack
// ---------------------------------------------------------------------------

/**
 * Pack a list of parts onto as few sheets as possible using the guillotine
 * algorithm (BSSF placement + SAS split).
 *
 * @param parts        - parts to pack (all must have the same materialId)
 * @param sheetW       - full sheet width (inches)
 * @param sheetH       - full sheet height (inches)
 * @param materialId   - material identifier (propagated to Sheet)
 * @param materialName - human-readable material name (propagated to Sheet)
 * @param kerf         - blade kerf allowance in inches (default 0.125)
 * @param border       - inset from sheet edges in inches (default 0)
 * @returns packed sheets and any parts that could not be placed
 */
export function guillotinePack(
  parts: Part[],
  sheetW: number,
  sheetH: number,
  materialId: string,
  materialName: string,
  kerf = DEFAULT_KERF,
  border = 0,
): { sheets: Sheet[]; skipped: Part[] } {
  const startTime = Date.now();

  // 1. Sort parts largest-first (by max dimension)
  const sorted = [...parts].sort(
    (a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height),
  );

  // 2. Usable area after border inset
  const usableW = sheetW - 2 * border;
  const usableH = sheetH - 2 * border;

  // 3. Precompute suffixMinDim[i] = smallest min(w,h) from index i onwards.
  //    Used to prune free rects that are too small for any remaining part.
  const n = sorted.length;
  const suffixMinDim = new Array<number>(n + 1).fill(Infinity);
  for (let i = n - 1; i >= 0; i--) {
    const p = sorted[i];
    suffixMinDim[i] = Math.min(
      Math.min(p.width, p.height),
      suffixMinDim[i + 1],
    );
  }

  const sheets: Sheet[] = [];
  const skipped: Part[] = [];

  // Helper: create a fresh sheet
  const newSheet = (): Sheet => ({
    width: sheetW,
    height: sheetH,
    materialId,
    materialName,
    placements: [],
    freeRects: [{ x: 0, y: 0, w: usableW, h: usableH }],
  });

  // Helper: place a part onto a sheet given a fit result
  const placePart = (
    sheet: Sheet,
    part: Part,
    fitIndex: number,
    rotated: boolean,
    partI: number,
  ): void => {
    const rect = sheet.freeRects[fitIndex];
    // Remove the chosen free rect
    sheet.freeRects.splice(fitIndex, 1);

    const placedW = rotated ? part.height : part.width;
    const placedH = rotated ? part.width : part.height;

    const placement: PlacedPart = {
      part,
      x: rect.x + border,
      y: rect.y + border,
      rotated,
      width: placedW,
      height: placedH,
    };
    sheet.placements.push(placement);

    // Split remaining space
    const newRects = splitRect(
      rect.x,
      rect.y,
      rect.w,
      rect.h,
      placedW,
      placedH,
      kerf,
    );
    sheet.freeRects.push(...newRects);

    // Prune rects that are too small for any remaining part
    const nextMinDim = suffixMinDim[partI + 1];
    if (nextMinDim < Infinity) {
      sheet.freeRects = sheet.freeRects.filter(
        (r) => Math.min(r.w, r.h) >= nextMinDim,
      );
    }
  };

  // 4. Main loop
  for (let i = 0; i < sorted.length; i++) {
    // Timeout safety (30 seconds)
    if (Date.now() - startTime > 30_000) {
      // Add all remaining parts to skipped
      skipped.push(...sorted.slice(i));
      break;
    }

    const part = sorted[i];
    const pw = part.width;
    const ph = part.height;

    // Check whether the part fits in any orientation on a fresh sheet
    const fitsNormal = usableW >= pw && usableH >= ph;
    const fitsRotated =
      part.canRotate && usableW >= ph && usableH >= pw;

    if (!fitsNormal && !fitsRotated) {
      // Part is too large for the sheet in any orientation — skip it
      skipped.push(part);
      continue;
    }

    // Try existing sheets first
    let placed = false;
    for (const sheet of sheets) {
      const fit = findBestFit(sheet.freeRects, pw, ph, kerf, part.canRotate);
      if (fit) {
        placePart(sheet, part, fit.index, fit.rotated, i);
        placed = true;
        break;
      }
    }

    if (!placed) {
      // Open a new sheet
      const sheet = newSheet();
      const fit = findBestFit(sheet.freeRects, pw, ph, kerf, part.canRotate);
      if (fit) {
        placePart(sheet, part, fit.index, fit.rotated, i);
        sheets.push(sheet);
      } else {
        // Should not happen given the pre-check above, but guard defensively
        skipped.push(part);
      }
    }
  }

  return { sheets, skipped };
}

// ---------------------------------------------------------------------------
// Sheet utility functions
// ---------------------------------------------------------------------------

/** Total area (sq in) occupied by all placed parts on a sheet. */
export function sheetUsedArea(sheet: Sheet): number {
  return sheet.placements.reduce((sum, p) => sum + p.width * p.height, 0);
}

/**
 * Packing efficiency of a sheet as a percentage (0–100).
 * Calculated as (used area / total sheet area) × 100.
 */
export function sheetEfficiency(sheet: Sheet): number {
  const total = sheet.width * sheet.height;
  if (total <= 0) return 0;
  return (sheetUsedArea(sheet) / total) * 100;
}
